import React from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Menu,
  MenuItem,
  Button,
  Link,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLogout } from '@refinedev/core';
import { useUser } from '@/context/UserContext';
import exxonLogoWhite from '@/assets/images/Exxon_Mobil_Logo_white.svg';

const NAV_LINKS = [
  { path: '/orders', label: 'Orders' },
  { path: '/inventory-management', label: 'Inventory Management' },
  { path: '/scheduling', label: 'Scheduling' },
  { path: '/supply-point', label: 'Supply Point' },
];

const INVENTORY_LINKS = [
  { view: 'availability', label: 'Product Availability' },
  { view: 'grading', label: 'Inventory Grading' },
  { view: 'replenisher', label: 'Replenishment' },
  { view: 'uploads', label: 'Uploads' },
];

export const NavBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate: logout } = useLogout();
  const { userData } = useUser();
  const [userAnchorEl, setUserAnchorEl] = React.useState<null | HTMLElement>(null);
  const [inventoryAnchorEl, setInventoryAnchorEl] = React.useState<null | HTMLElement>(null);
  const [supplyPointAnchorEl, setSupplyPointAnchorEl] = React.useState<null | HTMLElement>(null);

  const onSplashScreen = location.pathname === '/';
  const activeLinkIndex = NAV_LINKS.findIndex((link) => (
    location.pathname === link.path || location.pathname.startsWith(`${link.path}/`)
  )) + 1;

  const userTypeString = React.useMemo(() => {
    let value = '';
    if (userData.product && userData.product !== 'null') {
      value += `${userData.product} - `;
    }
    if (userData.region && userData.region !== 'null') {
      value += `${userData.region} - `;
    }
    if (userData.role && userData.role !== 'null' && userData.role.toLowerCase() !== 'viewer') {
      value += userData.role.split('/')[0].trim();
    }
    return value;
  }, [userData.product, userData.region, userData.role]);

  const mutedHeaderColor = 'rgba(255,255,255,0.5)';

  const navLinkSx = (index: number) => ({
    minWidth: 0,
    height: 64,
    px: { xs: '4px', md: '10px', lg: '16px' },
    mx: { xs: 0, md: '4px', lg: '8px' },
    flexShrink: 1,
    borderRadius: 0,
    color: activeLinkIndex === index ? '#fff' : 'rgba(255,255,255,0.7)',
    opacity: 1,
    fontSize: '16px',
    fontWeight: 400,
    letterSpacing: '0.02em',
    whiteSpace: { xs: 'normal', md: 'nowrap' },
    lineHeight: 1.2,
    textTransform: 'none',
    borderBottom: activeLinkIndex === index ? '5px solid #fff' : '5px solid transparent',
    mb: 0,
    boxSizing: 'border-box',
    pt: 0,
    pb: '5px',
    pointerEvents: onSplashScreen ? 'none' : 'auto',
    cursor: onSplashScreen ? 'default' : 'pointer',
    '&:hover': {
      backgroundColor: 'transparent',
      opacity: 1,
    },
    ...(onSplashScreen ? { color: mutedHeaderColor, opacity: 1 } : {}),
  });

  return (
    <AppBar
      position="fixed"
      sx={{
        height: '64px',
        bgcolor: '#0c479d',
        boxShadow: '0 4px 10px -6px black',
      }}
    >
      <Toolbar disableGutters sx={{ height: '100%', minHeight: '64px !important', px: 0, minWidth: 0, overflow: 'hidden' }}>
        <Box
          sx={{
            width: '100%',
            minWidth: 0,
            height: '100%',
            color: '#fff',
            display: 'flex',
            position: 'relative',
            zIndex: 10,
          }}
        >
          <Box sx={{ flex: '0 0 auto', mr: { xs: 1, md: 3, lg: 5 }, height: '100%' }}>
            <Link
              href="/"
              underline="none"
              sx={{
                color: '#fff',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <Box
                component="img"
                className="exxon-logo"
                src={exxonLogoWhite}
                alt="Exxon Mobil"
                sx={{
                  m: { xs: '2px 6px 0 6px', md: '2px 16px 0 10px' },
                  height: { xs: 16, sm: 24, md: 32 },
                  width: 'auto',
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ display: { xs: 'none', md: 'block' }, color: mutedHeaderColor, fontSize: '28px' }}>
                OTD
              </Typography>
            </Link>
          </Box>

          <Box component="nav" aria-label="Main navigation" sx={{ flex: '0 1 auto', minWidth: 0, height: '100%' }}>
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}>
              <Button
                id="qa-orders-lnk"
                className="nav-link"
                sx={navLinkSx(1)}
                onClick={(event) => {
                  if (onSplashScreen) {
                    event.preventDefault();
                    return;
                  }
                  navigate('/orders');
                }}
              >
                Orders
              </Button>

              <Button
                id="qa-inventory-management-lnk"
                className="nav-link"
                sx={navLinkSx(2)}
                aria-controls={inventoryAnchorEl ? 'inventory-management-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={inventoryAnchorEl ? 'true' : undefined}
                onClick={(event) => {
                  if (!onSplashScreen) setInventoryAnchorEl(event.currentTarget);
                }}
              >
                Inventory Management
              </Button>
              <Menu
                id="inventory-management-menu"
                anchorEl={inventoryAnchorEl}
                open={Boolean(inventoryAnchorEl)}
                onClose={() => setInventoryAnchorEl(null)}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                MenuListProps={{ 'aria-labelledby': 'qa-inventory-management-lnk' }}
                sx={{
                  '& .MuiPaper-root': { borderRadius: '4px', minWidth: 214, mt: '-3px' },
                  '& .MuiMenuItem-root': {
                    minHeight: 54,
                    px: '18px',
                    fontSize: '16px',
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                  },
                }}
              >
                {INVENTORY_LINKS.map(({ view, label }) => (
                  <MenuItem
                    key={view}
                    disabled={onSplashScreen}
                    onClick={() => {
                      setInventoryAnchorEl(null);
                      navigate(`/inventory-management?view=${view}`);
                    }}
                  >
                    {label}
                  </MenuItem>
                ))}
              </Menu>

              <Button
                id="qa-scheduling-lnk"
                className="nav-link"
                sx={navLinkSx(3)}
                onClick={(event) => {
                  if (onSplashScreen) {
                    event.preventDefault();
                    return;
                  }
                  navigate('/scheduling');
                }}
              >
                Scheduling
              </Button>

              <Button
                id="qa-supply-point-mnu"
                className="nav-link"
                sx={navLinkSx(4)}
                onClick={(event) => {
                  if (onSplashScreen) {
                    event.preventDefault();
                    return;
                  }
                  setSupplyPointAnchorEl(event.currentTarget);
                }}
              >
                Supply Point
              </Button>
              <Menu
                anchorEl={supplyPointAnchorEl}
                open={Boolean(supplyPointAnchorEl)}
                onClose={() => setSupplyPointAnchorEl(null)}
                sx={{ '& .MuiMenuItem-root': { fontSize: '16px' } }}
              >
                <MenuItem
                  id="qa-loading-btn"
                  disabled={onSplashScreen}
                  onClick={() => {
                    setSupplyPointAnchorEl(null);
                    navigate('/supply-point?type=loading');
                  }}
                >
                  Loading
                </MenuItem>
                <MenuItem
                  id="qa-deliveries-btn"
                  disabled={onSplashScreen}
                  onClick={() => {
                    setSupplyPointAnchorEl(null);
                    navigate('/supply-point?type=deliveries');
                  }}
                >
                  Receiving
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          <Box
            sx={{
              flex: '1 1 0',
              minWidth: { xs: 44, md: 60 },
              fontSize: '1.3em',
              mb: '5px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              pr: { xs: 0, md: 2 },
            }}
          >
            <Button
              id="qa-userDropDown-mnu"
              title={userTypeString}
              aria-label={userTypeString || 'User menu'}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: mutedHeaderColor,
                opacity: 1,
                textTransform: 'none',
                minWidth: 0,
                maxWidth: '100%',
                px: { xs: '4px', md: '8px' },
                '&:hover': {
                  opacity: 1,
                  backgroundColor: 'transparent',
                },
              }}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => setUserAnchorEl(e.currentTarget)}
            >
              <Typography sx={{ display: { xs: 'none', lg: 'block' }, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '16px', whiteSpace: 'nowrap' }}>
                {userTypeString}
              </Typography>
              <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'center', gap: '2px' }}>
                <AccountCircleIcon sx={{ fontSize: '1.35em', ml: { xs: 0, md: '10px' }, mr: '2px' }} />
                <ArrowDropDownIcon sx={{ fontSize: '1.2em' }} />
              </Box>
            </Button>

            <Menu
              anchorEl={userAnchorEl}
              open={Boolean(userAnchorEl)}
              onClose={() => setUserAnchorEl(null)}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              sx={{ '& .MuiMenuItem-root': { fontSize: '16px' } }}
            >
              <MenuItem
                id="qa-changeSettings-btn"
                disabled={onSplashScreen}
                onClick={() => {
                  setUserAnchorEl(null);
                  navigate('/');
                }}
              >
                Change Product and Role Settings
              </MenuItem>
              <MenuItem
                id="qa-signOut-lnk"
                component="a"
                href="/.auth/logout"
                onClick={() => {
                  setUserAnchorEl(null);
                  logout();
                }}
              >
                Sign Out
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
